import qs from "querystring";
import { useEffect, useState } from "react";
import useSWR from "swr";

import { FILTERED_STORE_IDS } from "@/shared/constants";

import { axios } from "@/utils/axios";
import { nextApiHostForLocale } from "@/utils/locale";
import logger from "@/utils/logger";

import { useCurrentLocale } from "@/context/LanguageContext";

import { API, ApiRes, Data } from "@/types/hooks";

export async function fetcher<T>(url: string, options?: Record<string, unknown>): Promise<T> {
    const res = await axios(url, options);
    return res.data;
}

export const useSearchStores = <T>(path: string, config: API<T>): ApiRes<T> => {
    const locale = useCurrentLocale();
    const host = nextApiHostForLocale(locale);
    const [error, setError] = useState<Error | null>(null);
    const [data, setData] = useState<Data<T>>(config?.initialState ?? { data: [], totalCount: 0 });
    const queryString = qs.stringify(config.query);
    const url = `${host}${path}?${queryString}`;

    const { data: swrData, error: swrError } = useSWR<Data<T>>(url, fetcher, {
        shouldRetryOnError: false,
        ...config.swrConfig,
    });

    if (swrError && swrError !== error) {
        logger.logException(swrError, "Unable to fetch paginated data");
        setError(swrError);
    }

    useEffect(() => {
        if (swrData === undefined) {
            return;
        }

        try {
            // Temporarily filter out Walmart and Sam's Club Gift Card Stores via store id (in useAutocomplete as well)
            // This is a hack. Please fix afterwards
            // @ts-ignore
            swrData.data = swrData.data.filter((store) => !FILTERED_STORE_IDS.includes(store.id));
            swrData.totalCount = swrData.data.length;

            setData({
                data: swrData.data,
                totalCount: swrData.totalCount,
            });

            setError(null);
        } catch (err) {
            const errorMessage = "Unable to transform searched stores";
            logger.logException(err, errorMessage);
            setError(new Error(errorMessage));
        }
    }, [swrData, swrError]);

    return { data, error };
};
